"use client"

import { useState, useEffect } from "react"
import axios from "axios"
import {
  SearchIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  PhotographIcon,
  TagIcon,
  UserIcon,
  DocumentTextIcon,
  XIcon,
  ArrowLeftIcon,
} from "@heroicons/react/outline"

const BlogAdmin = () => {
  const [blogs, setBlogs] = useState([])
  const [showModal, setShowModal] = useState(false)
  const [selectedBlog, setSelectedBlog] = useState(null)
  const [formData, setFormData] = useState({
    title: "",
    content: "",
    author: "",
    tags: "",
    image: null,
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [preview, setPreview] = useState(null)
  const [searchTerm, setSearchTerm] = useState("")
  const [activeTab, setActiveTab] = useState("list")

  const API_URL = "https://server.ananikaet.com/api/blogs"

  // Fetch blogs from the API
  const fetchBlogs = async () => {
    try {
      setLoading(true)
      const response = await axios.get(API_URL)
      setBlogs(response.data)
    } catch (error) {
      setError("Error fetching blogs. Please try again.")
      console.error("Error fetching blogs:", error)
    } finally {
      setLoading(false)
    }
  }

  // Open modal for creating or editing blog
  const openModal = (blog = null) => {
    setSelectedBlog(blog)
    setFormData(
      blog
        ? {
            ...blog,
            tags: blog.tags.join(", "),
          }
        : {
            title: "",
            content: "",
            author: "",
            tags: "",
            image: null,
          },
    )
    setPreview(null)
    setShowModal(true)
  }

  // Handle form field change
  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  // Handle file input change
  const handleFileChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      setFormData((prev) => ({ ...prev, image: file }))

      // Create preview
      const reader = new FileReader()
      reader.onloadend = () => {
        setPreview(reader.result)
      }
      reader.readAsDataURL(file)
    }
  }

  // Submit form to create or update blog
  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError(null)

    const blogData = new FormData()
    blogData.append("title", formData.title)
    blogData.append("content", formData.content)
    blogData.append("author", formData.author)
    blogData.append("tags", formData.tags)
    if (formData.image) {
      blogData.append("image", formData.image)
    }

    try {
      if (selectedBlog) {
        await axios.put(`${API_URL}/${selectedBlog._id}`, blogData)
        setSuccess("Blog updated successfully!")
      } else {
        await axios.post(API_URL, blogData)
        setSuccess("Blog created successfully!")
      }

      fetchBlogs()
      setShowModal(false)
      setActiveTab("list")

      // Auto-clear success message after 3 seconds
      setTimeout(() => {
        setSuccess(null)
      }, 3000)
    } catch (error) {
      setError("Error submitting blog. Please try again.")
      console.error("Error submitting blog:", error)
    } finally {
      setLoading(false)
    }
  }

  // Delete blog
  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this blog?")) {
      try {
        setLoading(true)
        await axios.delete(`${API_URL}/${id}`)
        setSuccess("Blog deleted successfully!")
        fetchBlogs()

        // Auto-clear success message after 3 seconds
        setTimeout(() => {
          setSuccess(null)
        }, 3000)
      } catch (error) {
        setError("Error deleting blog. Please try again.")
        console.error("Error deleting blog:", error)
      } finally {
        setLoading(false)
      }
    }
  }

  // Filter blogs based on search term
  const filteredBlogs = blogs.filter(
    (blog) =>
      blog.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      blog.author.toLowerCase().includes(searchTerm.toLowerCase()) ||
      blog.tags.some((tag) => tag.toLowerCase().includes(searchTerm.toLowerCase())),
  )

  // Fetch blogs when component mounts
  useEffect(() => {
    fetchBlogs()
  }, [])

  return (
<div className="flex flex-col min-h-screen px-4 pt-6 pb-6 lg:pl-64 bg-gray-50">
  {/* Sticky Heading - Added more top padding for mobile */}
  <div className="sticky top-0 z-10 bg-gray-50 pt-16 md:pt-2 pb-4 mb-6">
    <div className="flex justify-between items-center">
      <div>
        <h2 className="text-2xl md:text-3xl font-bold text-blue-900">Blog Management</h2>
        <div className="h-1 w-20 bg-blue-600 mt-2 rounded-full"></div>
      </div>

      {activeTab === "list" ? (
        <button
          onClick={() => setActiveTab("form")}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Create Blog
        </button>
      ) : (
        <button
          onClick={() => setActiveTab("list")}
          className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-2" />
          Back to List
        </button>
      )}
    </div>
  </div>


      {/* Alerts */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center text-red-700">
          <svg
            className="w-5 h-5 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          {error}
        </div>
      )}

      {success && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center text-green-700">
          <svg
            className="w-5 h-5 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          {success}
        </div>
      )}

      {activeTab === "form" && (
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h3 className="text-xl font-medium text-gray-800 mb-6">
            {selectedBlog ? "Edit Blog Post" : "Create New Blog Post"}
          </h3>

          <form onSubmit={handleSubmit} encType="multipart/form-data">
            <div className="grid gap-6 md:grid-cols-2">
              <div className="space-y-4">
                <div className="space-y-2">
                  <label htmlFor="title" className="flex items-center text-sm font-medium text-gray-700">
                    <DocumentTextIcon className="w-4 h-4 mr-2" />
                    Title
                  </label>
                  <input
                    id="title"
                    name="title"
                    type="text"
                    value={formData.title}
                    onChange={handleChange}
                    placeholder="Blog title"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <label htmlFor="author" className="flex items-center text-sm font-medium text-gray-700">
                    <UserIcon className="w-4 h-4 mr-2" />
                    Author
                  </label>
                  <input
                    id="author"
                    name="author"
                    type="text"
                    value={formData.author}
                    onChange={handleChange}
                    placeholder="Author name"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <label htmlFor="tags" className="flex items-center text-sm font-medium text-gray-700">
                    <TagIcon className="w-4 h-4 mr-2" />
                    Tags
                  </label>
                  <input
                    id="tags"
                    name="tags"
                    type="text"
                    value={formData.tags}
                    onChange={handleChange}
                    placeholder="Comma separated tags"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                  <p className="text-xs text-gray-500">Separate tags with commas (e.g., technology, news, tutorial)</p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="flex items-center text-sm font-medium text-gray-700">
                    <PhotographIcon className="w-4 h-4 mr-2" />
                    Featured Image
                  </label>
                  <div className="border-2 border-dashed rounded-lg p-4 hover:bg-gray-50 transition-colors cursor-pointer">
                    <label className="flex flex-col items-center justify-center cursor-pointer">
                      {preview ? (
                        <div className="relative w-full aspect-video mb-4">
                          <img
                            src={preview || "/placeholder.svg"}
                            alt="Preview"
                            className="rounded-md object-cover w-full h-full"
                          />
                        </div>
                      ) : (
                        <div className="flex flex-col items-center justify-center py-6">
                          <PhotographIcon className="w-12 h-12 text-gray-400 mb-2" />
                          <p className="text-sm text-gray-500 text-center">
                            Click to upload an image
                            <br />
                            <span className="text-xs">PNG, JPG or GIF</span>
                          </p>
                        </div>
                      )}
                      <input
                        type="file"
                        id="image"
                        name="image"
                        onChange={handleFileChange}
                        className="hidden"
                        accept="image/*"
                      />
                    </label>
                  </div>
                  {selectedBlog && !preview && (
                    <p className="text-xs text-yellow-500 mt-2 flex items-center">
                      <svg
                        className="w-3 h-3 mr-1"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 9v2m0 4h.01M-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                        />
                      </svg>
                      Leave empty to keep the current image
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-6 space-y-2">
              <label htmlFor="content" className="block text-sm font-medium text-gray-700">
                Content
              </label>
              <textarea
                id="content"
                name="content"
                value={formData.content}
                onChange={handleChange}
                placeholder="Write your blog content here..."
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[200px]"
                required
              />
            </div>

            <div className="mt-6 flex space-x-4">
              <button
                type="submit"
                disabled={loading}
                className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white min-w-[120px] ${
                  loading
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                }`}
              >
                {loading ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    {selectedBlog ? "Updating..." : "Creating..."}
                  </>
                ) : selectedBlog ? (
                  "Update Blog"
                ) : (
                  "Create Blog"
                )}
              </button>

              <button
                type="button"
                onClick={() => {
                  setSelectedBlog(null)
                  setActiveTab("list")
                }}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {activeTab === "list" && (
        <>
          {/* Search Bar */}
          <div className="mb-6 relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <SearchIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search blogs by title, author or tags..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Blog List */}
          {loading && blogs.length === 0 ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-900"></div>
            </div>
          ) : filteredBlogs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
              <DocumentTextIcon className="w-16 h-16 mb-3 opacity-20" />
              <p className="text-lg font-medium">No blogs found</p>
              <p className="text-sm mb-4">
                {searchTerm ? "Try adjusting your search" : "Create your first blog post to get started"}
              </p>
              {!searchTerm && (
                <button
                  onClick={() => setActiveTab("form")}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Create Blog
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredBlogs.map((blog) => (
                <div
                  key={blog._id}
                  className="bg-white rounded-lg overflow-hidden shadow-lg transition-all hover:shadow-xl"
                >
                  <div className="relative aspect-video">
                    <img
                      src={
                        blog.imageUrl
                          ? `https://server.ananikaet.com/uploads/${blog.imageUrl}`
                          : "https://via.placeholder.com/400x200"
                      }
                      alt={blog.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="text-lg font-semibold line-clamp-2 mb-1">{blog.title}</h3>
                    <div className="flex items-center text-sm text-gray-500 mb-2">
                      <UserIcon className="w-3 h-3 mr-1" />
                      {blog.author}
                    </div>
                    <p className="text-sm text-gray-600 line-clamp-2 mb-2">{blog.content}</p>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {blog.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="p-4 pt-0 flex justify-between">
                    <button
                      onClick={() => {
                        setSelectedBlog(blog)
                        setActiveTab("form")
                      }}
                      className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <PencilIcon className="h-4 w-4 mr-1" />
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(blog._id)}
                      className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-red-700 bg-red-50 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    >
                      <TrashIcon className="h-4 w-4 mr-1" />
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Blog View/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={() => setShowModal(false)}></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
              &#8203;
            </span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                      {selectedBlog ? "Edit Blog Post" : "Create New Blog Post"}
                    </h3>

                    <form onSubmit={handleSubmit} encType="multipart/form-data">
                      <div className="grid gap-4">
                        <div className="space-y-2">
                          <label htmlFor="modal-title" className="block text-sm font-medium text-gray-700">
                            Title
                          </label>
                          <input
                            id="modal-title"
                            name="title"
                            type="text"
                            value={formData.title}
                            onChange={handleChange}
                            placeholder="Blog title"
                            className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            required
                          />
                        </div>

                        <div className="space-y-2">
                          <label htmlFor="modal-content" className="block text-sm font-medium text-gray-700">
                            Content
                          </label>
                          <textarea
                            id="modal-content"
                            name="content"
                            value={formData.content}
                            onChange={handleChange}
                            placeholder="Write your blog content here..."
                            className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[150px]"
                            required
                          />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <label htmlFor="modal-author" className="block text-sm font-medium text-gray-700">
                              Author
                            </label>
                            <input
                              id="modal-author"
                              name="author"
                              type="text"
                              value={formData.author}
                              onChange={handleChange}
                              placeholder="Author name"
                              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              required
                            />
                          </div>

                          <div className="space-y-2">
                            <label htmlFor="modal-tags" className="block text-sm font-medium text-gray-700">
                              Tags
                            </label>
                            <input
                              id="modal-tags"
                              name="tags"
                              type="text"
                              value={formData.tags}
                              onChange={handleChange}
                              placeholder="Comma separated tags"
                              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              required
                            />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <label htmlFor="modal-image" className="block text-sm font-medium text-gray-700">
                            Featured Image
                          </label>
                          <input
                            id="modal-image"
                            name="image"
                            type="file"
                            onChange={handleFileChange}
                            accept="image/*"
                            className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                          {preview && (
                            <div className="mt-2 relative">
                              <img
                                src={preview || "/placeholder.svg"}
                                alt="Preview"
                                className="rounded-md max-h-[200px] object-contain"
                              />
                              <button
                                type="button"
                                className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 focus:outline-none"
                                onClick={() => {
                                  setPreview(null)
                                  setFormData((prev) => ({ ...prev, image: null }))
                                }}
                              >
                                <XIcon className="h-3 w-3" />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                        <button
                          type="submit"
                          disabled={loading}
                          className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white sm:ml-3 sm:w-auto sm:text-sm ${
                            loading
                              ? "bg-gray-400 cursor-not-allowed"
                              : "bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                          }`}
                        >
                          {loading ? (
                            <>
                              <svg
                                className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                ></circle>
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                ></path>
                              </svg>
                              {selectedBlog ? "Updating..." : "Creating..."}
                            </>
                          ) : selectedBlog ? (
                            "Update"
                          ) : (
                            "Create"
                          )}
                        </button>
                        <button
                          type="button"
                          className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm"
                          onClick={() => setShowModal(false)}
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default BlogAdmin

